/**
 * Vendors
 */
import React, { useState } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

/**
 * Service
 */
import { webmaster } from '../../../services/ApiService';

/**
 * Actions
 */
import { startSetEditProfile, startSetProfile, setTwoFactorAuth } from '@actions/webmaster';
import { openAlert } from '@actions/alerts';

/**
 * Components
 */

/**
 * View
 */
import { SecurityPageView } from '../index';

/**
 * Helpers
 */
import { handleResponseErrors, makeFields } from '@components/TwoFactorAuth/twoFactorAuthHelpers';

/**
 * PropTypes
 */
const propTypes = {
  intl: intlShape.isRequired,
};

/**
 * Expo
 */
const SecurityPage = ({ intl }) => {
  /**
   * Store
   */
  const profile = useSelector(({ webmaster }) => webmaster.profile);
  const two_factor = useSelector(({ webmaster }) => webmaster.profile.two_factor);
  const dispatch = useDispatch();

  /**
   * State
   */
  const [isLoading, setLoadingState] = useState(false);
  const [isActivating, setActivatingState] = useState(false);
  const [responseInfo, setResponseInfo] = useState({});
  const [showBotInfo, setBotInfoState] = useState(false);
  const [botsInfo, setBotsInfo] = useState({});

  const omitErrorMessage = (response = {}) => {
    response.message = null;
    return response;
  };

  const onSubmit = (code) => {
    const { sessionId } = responseInfo;
    const { type, is_active } = two_factor;
    const fields = makeFields(type, code, sessionId);

    !!is_active ? disableTwoFactorAuth(fields, false) : activateTwoFactorAuth(null, fields, false);
  };

  const enableTwoFactorAuth = (type) => {
    setLoadingState(true);
    const auth_type = two_factor.type === type ? 0 : type;

    const updatedProfile = { ...profile, auth_type };

    dispatch(startSetEditProfile(updatedProfile))
      .then((response) => {
        dispatch(startSetProfile()).then(() => {
          setLoadingState(false);
        });
      })
      .catch((err) => console.log(err));
  };

  const disableTwoFactorAuth = (fields, isOmitMessage = true) => {
    if (fields) {
      setLoadingState(true);
    }
    setActivatingState(true);
    const auth_type = 0;

    const updatedProfile = { ...profile, auth_type, ...fields };

    dispatch(startSetEditProfile(updatedProfile))
      .then((response) => {
        dispatch(startSetProfile()).then(() => {
          setLoadingState(false);
          successHandler('disabled');
        });
      })
      .catch((err) => {
        setLoadingState(false);
        let response = handleResponseErrors(err);

        if (!response) {
          handleError(err);
          return;
        }

        if (isOmitMessage) {
          response = omitErrorMessage(response);
        }

        setResponseInfo(response);
      });
  };

  const handleError = (err) => {
    const message = err?.response?.data?.error?.message?.message;
    dispatch(
      openAlert({
        alertType: 'danger',
        message: message || err.toString(),
        dismissable: true,
      }),
    );
  };

  const successHandler = (type) => {
    dispatch(
      openAlert({
        alertType: 'success',
        message: intl.formatMessage({
          id: `pages.profile.security.${type}`,
          defaultMessage: 'Two-factor authentication activated',
        }),
        dismissable: true,
        closeInSeconds: 5,
      }),
    );
  };

  /**
   *
   * @param {Event} e
   * @param {Object} args params for request
   * @param {Boolean} isOmitMessage should omit error message
   */
  const activateTwoFactorAuth = (e, args = {}, isOmitMessage = true) => {
    webmaster
      .activateTwoFactorAuth({ value: 1, ...args })
      .then(() => {
        setLoadingState(true);
        setActivatingState(false);
        successHandler('success');
        setBotInfoState(false);

        dispatch(startSetProfile()).then(() => {
          setLoadingState(false);
        });
      })
      .catch((err) => {
        let response = handleResponseErrors(err);

        if (!response) {
          handleError(err);
          return;
        }
        if (response.showTelegramBotInfo) {
          webmaster
            .getBots()
            .then(({ items }) => {
              setBotsInfo(items[0]);
              setBotInfoState(true);
            })
            .catch((err) => {
              handleError(err);
              return;
            });
          return;
        }
        setActivatingState(true);
        if (isOmitMessage) {
          response = omitErrorMessage(response);
        }

        setResponseInfo(response);
      });
  };

  const isAuthMethodEnabled = (type) => two_factor.type === type || two_factor.type === 3;

  const isActivateButtonRendering = () => two_factor.type !== 0 && two_factor.is_active === 0;

  return (
    <SecurityPageView
      showBotInfo={showBotInfo}
      botsInfo={botsInfo}
      isLoading={isLoading}
      isActivating={isActivating}
      setActivatingState={setActivatingState}
      two_factor={two_factor}
      profile={profile}
      enableTwoFactorAuth={enableTwoFactorAuth}
      disableTwoFactorAuth={disableTwoFactorAuth}
      isAuthMethodEnabled={isAuthMethodEnabled}
      activateTwoFactorAuth={activateTwoFactorAuth}
      isActivateButtonRendering={isActivateButtonRendering}
      responseInfo={responseInfo}
      onSubmit={onSubmit}
    />
  );
};

export default injectIntl(SecurityPage);
